// Svelte-free by design, so a `node:test` file can exercise it.

/**
 * Le funzioni a disponibilità limitata, lato interfaccia.
 *
 * Una funzione è accesa per un account (non per un'azienda e non per un
 * ruolo) e il server lo dice nelle `capabilities`: `features: [{ key, kind }]`.
 * L'interfaccia mostra una pagina o un comando solo se la sua chiave c'è;
 * il server rifiuta comunque ogni richiesta di chi non l'ha (403
 * `feature_not_enabled`), quindi nascondere è cortesia, non protezione.
 *
 * Copia speculare di src/domain/features.js: test/frontend-feature-gates.test.js
 * la tiene allineata. I testi in italiano stanno in i18n/locales/it.json
 * sotto `features.<chiave>`.
 */

export const FEATURE_KEYS = Object.freeze(['bilancio', 'anticipo-fattura']);

/** `experimental`: anteprima aperta a poco a poco. `dedicated`: riservata a chi indica il gestore. */
export const FEATURE_KINDS = Object.freeze(['experimental', 'dedicated']);

/**
 * Le funzioni che l'account ha, come le dice il server. Una risposta non
 * ancora arrivata, o senza l'elenco, non ne ha nessuna: meglio un comando che
 * compare un attimo dopo che uno che il server rifiuterebbe.
 *
 * @param {{ features?: Array<{ key?: string, kind?: string }> }|null|undefined} capabilities
 * @returns {Array<{ key: string, kind: string }>}
 */
export function featuresOf(capabilities) {
  const list = capabilities?.features;
  if (!Array.isArray(list)) return [];
  return list.filter((entry) => FEATURE_KEYS.includes(entry?.key) && FEATURE_KINDS.includes(entry?.kind));
}

/** Se l'account ha la funzione. */
export function hasFeature(capabilities, key) {
  return featuresOf(capabilities).some((entry) => entry.key === key);
}

/**
 * Cosa deve mostrare una pagina che esiste solo con una funzione.
 *
 * Quattro stati, perché «non lo sappiamo ancora» e «non siamo riusciti a
 * chiederlo» non sono un «no»:
 *   - `loading`: la risposta non è arrivata; si aspetta, senza scadenza;
 *   - `error`: la richiesta è fallita; si mostra uno stato da riprovare;
 *   - `denied`: il server ha risposto e la funzione non c'è;
 *   - `enabled`: il server ha risposto e la funzione c'è.
 * Solo `denied` porta alla pagina «funzione non disponibile», e solo con una
 * risposta del server in mano: `ready` e un valore confermato. Il segnaposto
 * che lo store tiene dopo un errore (`unconfirmed`) non è una risposta, quindi
 * durante un nuovo tentativo si resta in `loading`, non in `denied`.
 *
 * Prima delle capacità viene l'azienda, da cui dipendono: se l'elenco delle
 * aziende si sta caricando si aspetta, se non si è riusciti a caricarlo è un
 * errore da riprovare, e solo un elenco arrivato e davvero vuoto (`none`) non
 * lascia nulla da chiedere.
 *
 * @param {object|null|undefined} capabilities - il valore dello store, `null` finché non arriva
 * @param {'loading'|'ready'|'error'} status
 * @param {string} key
 * @param {{ company?: 'active'|'loading'|'error'|'none' }} [context]
 * @returns {'loading'|'error'|'denied'|'enabled'}
 */
export function featureAccess(capabilities, status, key, { company = 'active' } = {}) {
  if (company === 'loading') return 'loading';
  if (company === 'error') return 'error';
  if (company === 'none') return 'denied';
  if (status === 'error') return 'error';
  if (capabilities === null || capabilities === undefined || capabilities.unconfirmed === true) return 'loading';
  if (status !== 'ready' && status !== 'loading') return 'loading';
  return hasFeature(capabilities, key) ? 'enabled' : 'denied';
}

/**
 * Lo stato dell'azienda da cui dipende la risposta delle capacità, per
 * `featureAccess`. Un elenco di aziende non ancora chiesto (`idle`) o in
 * arrivo è «si aspetta»; fallito è «da riprovare»; arrivato e vuoto è l'unico
 * caso in cui non c'è nulla da chiedere.
 *
 * @param {string} activeCompanyId
 * @param {'idle'|'loading'|'ready'|'error'} companiesStatus
 * @returns {'active'|'loading'|'error'|'none'}
 */
export function companyState(activeCompanyId, companiesStatus) {
  if (activeCompanyId) return 'active';
  if (companiesStatus === 'ready') return 'none';
  if (companiesStatus === 'error') return 'error';
  return 'loading';
}

/**
 * Come presentare la sezione Anticipo fatture di una fattura: niente finché
 * non si sa (nessun lampo di contenuto) o se la funzione manca, la sezione se
 * c'è, un piccolo comando per riprovare se non si è riusciti a saperlo.
 *
 * @param {boolean} eligible - la fattura è di quelle per cui l'anticipo ha senso
 * @param {'loading'|'error'|'denied'|'enabled'} access - da `featureAccess`
 * @returns {'none'|'section'|'retry'}
 */
export function anticipoView(eligible, access) {
  if (!eligible) return 'none';
  if (access === 'enabled') return 'section';
  if (access === 'error') return 'retry';
  return 'none';
}

/**
 * Il tipo di una funzione, anche se l'account non la ha: serve alla pagina
 * «funzione non disponibile» per dire cosa significa. `null` per una chiave
 * sconosciuta.
 */
export function featureKind(key) {
  return { bilancio: 'experimental', 'anticipo-fattura': 'dedicated' }[key] ?? null;
}

/** Se una chiave è una funzione che questa versione conosce. */
export function isKnownFeature(key) {
  return FEATURE_KEYS.includes(key);
}
